import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { MessageTimeline } from "./messageTimeline.js";

export interface ChatThreadProps<TMessage> {
  messages: readonly TMessage[];
  /** True while loading thread history (shows a typing indicator). */
  isLoading?: boolean;
  /** Replaces the default typing indicator shown while loading. */
  loadingIndicator?: ReactNode;
  /** Renders a single message; typically a `ChatMessage`. */
  renderMessage: (message: TMessage, index: number, count: number) => ReactNode;
  /** Shown when there are no messages and nothing is loading. */
  emptyState?: ReactNode;
  /** Composer rendered below the scroll region (omit for view-only threads). */
  composer?: ReactNode;
  /** Optional sticky header above the scroll region. */
  header?: ReactNode;
  /** Gradient fades at the scroll region's edges while content is scrolled under them. */
  showsEdgeFades?: boolean;
  /** Styles the scrollable timeline region itself (e.g. padding overrides). */
  timelineClassName?: string;
  /** Styles the messages column (e.g. the gap between messages). */
  messagesClassName?: string;
  className?: string;
}

/**
 * Scrollable thread shell: optional header, an empty state or the scroll-
 * anchored message list, and a composer. Wraps `MessageTimeline` so every chat
 * surface reuses the same scroll/loading behavior instead of re-deriving
 * assistant-ui's `ThreadPrimitive.Viewport`.
 */
export function ChatThread<TMessage>(props: ChatThreadProps<TMessage>) {
  const { messages, isLoading = false, showsEdgeFades = false } = props;
  const showEmptyState =
    messages.length === 0 && !isLoading && props.emptyState !== undefined;

  return (
    <div
      className={twMerge(
        "flex min-h-0 flex-1 flex-col bg-white",
        props.className,
      )}
    >
      {props.header}

      {showEmptyState ? (
        <div className="flex flex-1 flex-col overflow-y-auto">
          {props.emptyState}
        </div>
      ) : (
        <MessageTimeline
          messages={messages}
          isLoading={isLoading}
          loadingIndicator={props.loadingIndicator}
          showsEdgeFades={showsEdgeFades}
          containerClassName="flex-1"
          messagesClassName={props.messagesClassName}
          className={twMerge(
            !showsEdgeFades && "flex-1",
            props.timelineClassName,
          )}
        >
          {props.renderMessage}
        </MessageTimeline>
      )}

      {props.composer}
    </div>
  );
}
